import { useEffect, useState } from 'react';
import { Alert, Button, CircularProgress, Link, Stack } from '@mui/material';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { authApi } from '@/api/auth.api';
import AuthShell from '@/components/layout/AuthShell';
import { humanizeError } from '@/utils/errorMessage';

/** Email verification — POST /api/auth/verify-email (code + email + company from the link). */
export default function VerifyEmailPage() {
  const [params] = useSearchParams();
  const code = params.get('code') ?? '';
  const email = params.get('email') ?? '';
  // The company the link was sent from: the visitor is not signed in, so nothing else names it.
  const company = params.get('company') ?? undefined;
  const [status, setStatus] = useState<'idle' | 'loading' | 'ok' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const verify = async () => {
    setStatus('loading');
    try {
      await authApi.verifyEmail({ code, email, tenantCode: company });
      setStatus('ok');
    } catch (e) {
      setStatus('error');
      setMessage(humanizeError(e, 'Verification failed.'));
    }
  };

  useEffect(() => {
    if (code && email) verify();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AuthShell
      title="Verify your email"
      subtitle="Confirming your email address."
      footer={<Link component={RouterLink} to="/login" variant="body2">← Back to sign in</Link>}
    >
      <Stack spacing={2}>
        {status === 'loading' && <Stack alignItems="center" sx={{ py: 3 }}><CircularProgress /></Stack>}
        {status === 'ok' && <Alert severity="success">Your email has been verified. Sign in with this address from now on.</Alert>}
        {status === 'error' && <Alert severity="error">{message}</Alert>}
        {(status === 'idle' || status === 'error') && (
          <Button variant="contained" size="large" onClick={verify} disabled={!code || !email}>
            {!code || !email ? 'Invalid verification link' : 'Verify email'}
          </Button>
        )}
      </Stack>
    </AuthShell>
  );
}
